import React, { useState } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import PaymentModal from 'components/admin/PaymentModal';
import AddPaymentModal from 'components/admin/AddPaymentModal';
import AdminAPI from 'config/api/AdminAPI';
import EnergyAPI from 'config/api/EnergyAPI';
import Chip from '@material-ui/core/Chip';
import Loader from 'components/general/Loader';

const useStyles = makeStyles((theme) => ({
	tableHeadCell: {
		fontWeight: 600,
		fontSize: '0.75rem',
		color: '#525f7f',
	},
	tableCell: {
		color: '#525f7f',
	},
	companyButton: {
		textTransform: 'none',
		padding: 0,
		minWidth: 0,
		color: theme.palette.primary.main,
		'&:hover': {
			textDecoration: 'underline',
		},
	},
	title: {
		fontSize: '1.4rem',
		fontWeight: 700,
		color: theme.palette.text.primary,
	},
	buttonSpacing: {
		marginLeft: theme.spacing(1),
	},
	addPaymentText: {
		color: theme.palette.primary.main,
		textDecoration: 'underline',
		cursor: 'pointer',
		fontSize: '0.9rem',
		fontWeight: 400,
		marginTop: theme.spacing(1),
		display: 'inline-block',
		'&:hover': {
			textDecoration: 'underline',
			color: theme.palette.primary.dark,
		},
	},
	bottomButtonRow: {
		paddingTop: theme.spacing(3),
		paddingBottom: 0,
	},
}));

function PendingTab({
	fsPayments,
	solarPayments,
    fsCompanies,
    solarCompanies,
	updatePayment,
    refreshPayments,
	showSuccessAlert
}) {
	const classes = useStyles();
	const [selectedPayment, setSelectedPayment] = useState(null);
	const [selectedType, setSelectedType] = useState(null);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
	const [addPaymentType, setAddPaymentType] = useState(null);
	// Remove tableLoading, add loading state for each payment by reference
	const [loadingPayments, setLoadingPayments] = useState({});
	const api = new AdminAPI();
	const eApi = new EnergyAPI();

	console.log('companies: ', fsCompanies, solarCompanies)

	function openPaymentModal(payment, type) {
		setSelectedPayment(payment);
		setSelectedType(type);
		setShowPaymentModal(true);
	}

	function closePaymentModal() {
		setShowPaymentModal(false);
		setSelectedPayment(null);
		setSelectedType(null);
	}

	function openAddPaymentModal(type) {
		console.log('add payment type: ', type)
		setAddPaymentType(type);
		setShowAddPaymentModal(true);
	}

	function closeAddPaymentModal() {
		setShowAddPaymentModal(false);
		setAddPaymentType(null);
	}

	async function handleGenerate(payment, type, autoCharge = false) {
		const ref = payment.reference || payment.id || payment._id || payment.date || Math.random();
		setLoadingPayments((prev) => ({ ...prev, [ref]: autoCharge ? 'charge' : 'send' }));
		try {
			let response = null
			if (type === 'Financial') {
				response = await api.generateInvoice(payment, autoCharge);
			} else {
				response = await eApi.generateInvoice(payment);
			}
			await refreshPayments();
			console.log('response: ', response);

			// Handle structured response with step-by-step results
			if (response && response.steps) {
				const steps = response.steps;
				let issues = [];

				if (!steps.xeroInvoice.success) {
					issues.push('Xero invoice: ' + (steps.xeroInvoice.error || 'Failed'));
				}
				if (!steps.stripeCharge.skipped && !steps.stripeCharge.success) {
					issues.push('Stripe charge: ' + (steps.stripeCharge.error || 'Failed'));
				}
				if (!steps.markPaid.skipped && !steps.markPaid.success) {
					issues.push('Mark paid: ' + (steps.markPaid.error || 'Failed'));
				}
				if (!steps.emailSent.success) {
					issues.push('Email: ' + (steps.emailSent.error || 'Failed'));
				}

				if (response.success) {
					let successMsg = 'Invoice generated successfully!';
					if (steps.stripeCharge.success && steps.stripeCharge.status === 'succeeded') {
						successMsg = 'Invoice generated and payment charged successfully!';
					} else if (steps.stripeCharge.success && steps.stripeCharge.status === 'processing') {
						successMsg = 'Invoice generated. Payment is processing (e.g. Direct Debit).';
					} else if (steps.stripeCharge.skipped) {
						successMsg = 'Invoice generated and sent. No card on file — payment link sent.';
					}
					if (issues.length > 0) {
						successMsg += '\n\nWarnings:\n' + issues.join('\n');
					}
					showSuccessAlert(successMsg);
				} else {
					alert('Invoice generation had issues:\n\n' + issues.join('\n'));
				}
			}
			// Legacy response format (fallback)
			else if (response) {
				showSuccessAlert('Invoice generated successfully!');
			}
			else {
				alert('Failed to generate invoice.');
			}
		} catch (e) {
			alert('Failed to generate invoice.');
		} finally {
			setLoadingPayments((prev) => ({ ...prev, [ref]: false }));
		}
	}

	function handleGenerateAll(payments) {
		// Generation logic to be implemented by you
	}

	function renderTable(payments, type) {
		return (
			<Card style={{ marginBottom: '2rem' }}>
				<CardHeader
					title={
						<Typography variant="h3" component="span" style={{ fontWeight: 700 }}>
							{type === 'Financial' ? 'Financial Payments' : 'Energy Payments'}
						</Typography>
					}
					action={
						<Button
							variant="outlined"
							color="primary"
							size="small"
							onClick={() => openAddPaymentModal(type)}
						>
							Add {type === 'Financial' ? 'Financial' : 'Energy'} Payment
						</Button>
					}
				/>
				<CardContent>
					<TableContainer>
						<Table size="small">
							{payments.length > 0 && (
								<TableHead>
									<TableRow>
										<TableCell className={classes.tableHeadCell}>Company</TableCell>
										<TableCell className={classes.tableHeadCell}>Invoice Date</TableCell>
										<TableCell className={classes.tableHeadCell}>Billing Period</TableCell>
										<TableCell className={classes.tableHeadCell}>Description</TableCell>
										<TableCell className={classes.tableHeadCell} align="right">Total (€)</TableCell>
										<TableCell className={classes.tableHeadCell} align="center">Action</TableCell>
									</TableRow>
								</TableHead>
							)}
							<TableBody>
								{payments.length === 0 ? (
									<TableRow>
										<TableCell colSpan={6} align="center" className={classes.tableCell}>No pending {type} invoices.<br/><br/>Weekly invoices are generated on Friday evening at 23:00 and monthly invoices on the 1st of the month.</TableCell>
									</TableRow>
								) : (
									<>
										{payments.map((payment, idx) => {
											const ref = payment.reference || payment.id || payment._id || payment.date || idx;
											return (
												<TableRow key={ref}>
													<TableCell className={classes.tableCell}>
														<Button className={classes.companyButton} color="primary" onClick={() => openPaymentModal(payment, type)}>
															{payment.companyName}
														</Button>
														{payment.billingName && (
															<Chip
																label={`Billed to ${payment.billingName}`}
																size="small"
																style={{
																	marginLeft: '8px',
																	backgroundColor: '#11cdef',
																	color: '#fff',
																	fontWeight: 600,
																	fontSize: '0.65rem',
																	height: '20px',
																}}
															/>
														)}
													</TableCell>
													<TableCell className={classes.tableCell}>{payment.date ? new Date(payment.date).toLocaleDateString() : ''}</TableCell>
													<TableCell className={classes.tableCell}>{payment.billingPeriod}</TableCell>
													<TableCell className={classes.tableCell}>
														{getDescription(payment.allocation)}
														{type === 'Solar' && payment.date >= '2026-04-10' && (
															<>
																{' '}
																<Chip
																	label={getBillingType(payment)}
																	size="small"
																	style={{
																		backgroundColor: getBillingTypeStyle(payment).bg,
																		color: getBillingTypeStyle(payment).color,
																		fontWeight: 600,
																		fontSize: '0.65rem',
																		height: '20px',
																	}}
																/>
															</>
														)}
													</TableCell>
													<TableCell className={classes.tableCell} align="right">
														€{Number(payment.total).toFixed(2)}
														{payment.notes && payment.notes.includes('Surge') && (
															<Box fontSize='10px' color='#5e72e4' fontWeight={600} marginTop='2px'>
																{payment.notes}
															</Box>
														)}
													</TableCell>
													<TableCell className={classes.tableCell} align="center">
														{/* Energy generate-invoice always attempts the Stripe charge, so label it honestly */}
														<Button
															variant="contained"
															color={type === 'Solar' ? 'primary' : 'secondary'}
															size="small"
														onClick={() => handleGenerate(payment, type, false)}
														disabled={!!loadingPayments[ref]}
													>
														{loadingPayments[ref] === 'send'
															? (type === 'Solar' ? 'Charging...' : 'Generating...')
															: (type === 'Solar' ? 'Generate & Charge' : 'Generate & Send')}
													</Button>
													{type === 'Financial' && (
														<Button
															variant="contained"
															color="primary"
															size="small"
															className={classes.buttonSpacing}
															onClick={() => handleGenerate(payment, type, true)}
															disabled={!!loadingPayments[ref] || payment.companyId === 'ai'}
														>
															{loadingPayments[ref] === 'charge' ? 'Charging...' : 'Generate & Charge'}
														</Button>
													)}
													</TableCell>
												</TableRow>
											);
										})}
										{/* Bottom row for Generate & Action All */}
										<TableRow>
											<TableCell colSpan={6} align="center" className={classes.bottomButtonRow}>
												{payments.length > 0 && (
													<Button
														disabled
														variant="contained"
														color="primary"
														size="small"
														onClick={() => handleGenerateAll(payments)}
													>
														Generate & Action All
													</Button>
												)}
											</TableCell>
										</TableRow>
									</>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</CardContent>
			</Card>
		);
	}

	function getDescription(allocations) {
		let description = '';
		if (allocations) {
			for (let [product, values] of Object.entries(allocations)) {
				let shortened = '';
				if (product === 'life-insurance') shortened = 'LC';
				if (product === 'income-protection') shortened = 'IP';
				if (product === 'pensions') shortened = 'PEN';
				if (product === 'mortgage-protection') shortened = 'MP';
				if (product === 'health-insurance') shortened = 'HI';
				if (product === 'home-insurance') shortened = 'HOME';
				if (product === 'car-insurance') shortened = 'CAR';
				if (product === 'business-insurance') shortened = 'BUS';
				if (product === 'gadget-insurance') shortened = 'GAD';
				if (product === 'pet-insurance') shortened = 'PET';
				if (product === 'travel-insurance') shortened = 'TRAV';
				if (product === 'start-pensions') shortened = 'SP';
				if (product === 'Solar') shortened = 'Solar';
				if (product === 'Insulation') shortened = 'INS';
				if (product === 'EV Charger') shortened = 'EV';
				if (product === 'Boiler') shortened = 'BOIL';
				if (product === 'Heat Pump') shortened = 'HP';
				if (parseInt(values.allocation) > 0) {
					description += shortened + 'x' + values.allocation + ' ';
				}
			}
		} else {
			description = 'Topup';
		}
		return description.trim();
	}

	function isAllocationBased(payment) {
		return payment.allocation && Object.values(payment.allocation).some(v => v.isAllocationBased || v.hasOwnProperty('actualDelivered'));
	}

	function getBillingType(payment) {
		if (isAllocationBased(payment)) return 'Allocation';
		return 'Actual';
	}

	function getBillingTypeStyle(payment) {
		if (isAllocationBased(payment)) return { bg: '#cce5ff', color: '#004085' };
		return { bg: '#d4edda', color: '#155724' };
	}

	return (
		<Box>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					{renderTable(fsPayments, 'Financial')}
				</Grid>
				<Grid item xs={12}>
					{renderTable(solarPayments, 'Solar')}
				</Grid>
			</Grid>
			{showPaymentModal &&
                <PaymentModal 
                    isOpen={showPaymentModal}
                    close={() => closePaymentModal()}
                    payment={selectedPayment}
                    updatePayment={(payment) => updatePayment(selectedType, payment)}
                    refreshPayments={() => refreshPayments()}
                    showSuccessAlert={(text) => showSuccessAlert(text)}
                    product={selectedType}
                />
            }
            {showAddPaymentModal &&
                <AddPaymentModal 
                    isOpen={showAddPaymentModal}
                    close={closeAddPaymentModal}
                    showSuccessAlert={(text) => showSuccessAlert(text)}
                    refreshPayments={() => refreshPayments()}
                    product={addPaymentType}
                    companies={addPaymentType === 'Financial' ? fsCompanies : solarCompanies}
                />
            }
		</Box>
	);
}

export default PendingTab;
